import { createFileRoute, Link } from "@tanstack/react-router";
import { safeLocalStorageRemove } from "@/lib/safe-browser";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { title: "Cookie Policy — Routify" },
      { name: "description", content: "How Routify uses cookies, local storage and similar technologies." },
    ],
  }),
  component: CookiesPage,
});

function CookiesPage() {
  function reset() {
    try {
      safeLocalStorageRemove("routify_cookie_consent");
      location.reload();
    } catch { /* ignore */ }
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-16 text-foreground">
      <Link to="/legal" className="text-sm text-muted-foreground hover:text-foreground">← Legal</Link>
      <h1 className="mt-6 text-3xl font-semibold">Cookie Policy</h1>
      <p className="mt-2 text-xs text-muted-foreground">Last updated / Última atualização: 2026-07-19 · Version 2.1</p>

      <section className="mt-8 space-y-5 text-sm leading-relaxed">
        <h2 className="text-lg font-semibold">1. What is a cookie?</h2>
        <p>
          A cookie is a small text file placed on your device when you visit a website. We also
          use similar technologies such as <strong>local storage</strong> and{" "}
          <strong>session storage</strong>, which work the same way for the purposes of Article
          5(3) of Directive 2002/58/EC ("ePrivacy") and Article 5 of Portuguese Law 41/2004.
        </p>

        <h2 className="text-lg font-semibold">2. Categories we use</h2>
        <p>
          <strong>Strictly necessary</strong> technologies are exempt from consent. All other
          technologies are only activated after you give consent through the cookie banner.
        </p>

        <h2 className="text-lg font-semibold">3. Detailed list</h2>

        <h3 className="text-base font-semibold mt-2">3.1 Strictly necessary (no consent required)</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Purpose</th>
                <th className="text-left p-2">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr><td className="p-2">routify_cookie_consent</td><td className="p-2">localStorage</td><td className="p-2">Stores your cookie banner choice</td><td className="p-2">Until cleared</td></tr>
              <tr><td className="p-2">routify_anon_id</td><td className="p-2">localStorage</td><td className="p-2">Anonymous identifier for quota and abuse prevention</td><td className="p-2">Until cleared</td></tr>
              <tr><td className="p-2">routify_lang</td><td className="p-2">localStorage</td><td className="p-2">Remembers your interface language</td><td className="p-2">Until cleared</td></tr>
              <tr><td className="p-2">sb-*-auth-token</td><td className="p-2">localStorage</td><td className="p-2">Authentication session (Supabase)</td><td className="p-2">Up to 1 year</td></tr>
              <tr><td className="p-2">__cf_bm</td><td className="p-2">Cookie (Cloudflare)</td><td className="p-2">Bot management / security</td><td className="p-2">30 min</td></tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-base font-semibold mt-4">3.2 Analytics (consent required)</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Provider</th>
                <th className="text-left p-2">Purpose</th>
                <th className="text-left p-2">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr><td className="p-2">_ga</td><td className="p-2">Google Analytics</td><td className="p-2">Distinguishes unique visitors</td><td className="p-2">2 years</td></tr>
              <tr><td className="p-2">_ga_&lt;ID&gt;</td><td className="p-2">Google Analytics</td><td className="p-2">Session state</td><td className="p-2">2 years</td></tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-base font-semibold mt-4">3.3 Third-party services loaded on interaction</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2">Service</th>
                <th className="text-left p-2">When loaded</th>
                <th className="text-left p-2">Purpose</th>
                <th className="text-left p-2">Privacy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr><td className="p-2">Google Maps</td><td className="p-2">When you open the map</td><td className="p-2">Maps, places, directions</td><td className="p-2"><a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">policies.google.com</a></td></tr>
              <tr><td className="p-2">Stripe</td><td className="p-2">On checkout</td><td className="p-2">Payment processing, fraud prevention</td><td className="p-2"><a className="underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a></td></tr>
            </tbody>
          </table>
        </div>

        <h2 className="text-lg font-semibold">4. Manage your choice</h2>
        <p>
          You can withdraw or change your cookie consent at any time by clicking the button
          below. You can also delete cookies through your browser settings (most browsers let
          you block all cookies, but the Service may not work properly without strictly
          necessary cookies).
        </p>
        <button onClick={reset} className="rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent">
          Reset cookie preferences
        </button>

        <h2 className="text-lg font-semibold">5. Changes</h2>
        <p>
          This Cookie Policy may be updated. The "Last updated" date above shows the current
          version.
        </p>

        <hr className="my-8 border-border" />

        <h2 className="text-xl font-semibold">Versão em Português</h2>

        <h3 className="text-base font-semibold mt-4">1. O que é um cookie?</h3>
        <p>
          Um cookie é um pequeno ficheiro de texto colocado no seu dispositivo quando visita um
          site. Usamos também tecnologias equivalentes (localStorage, sessionStorage),
          abrangidas pelo art. 5.º, n.º 3 da Diretiva 2002/58/CE ("ePrivacy") e pelo art. 5.º
          da Lei n.º 41/2004.
        </p>

        <h3 className="text-base font-semibold mt-4">2. Categorias</h3>
        <p>
          Tecnologias <strong>estritamente necessárias</strong> não exigem consentimento; todas
          as restantes só são ativadas após o consentimento dado no banner.
        </p>

        <h3 className="text-base font-semibold mt-4">3. Lista detalhada</h3>
        <p>
          A lista detalhada encontra-se acima (tabelas 3.1 a 3.3): tecnologias essenciais
          (consentimento, identificador anónimo de quota, idioma, sessão de autenticação,
          segurança Cloudflare), analítica (Google Analytics, mediante consentimento) e
          serviços de terceiros carregados na interação (Google Maps, Stripe).
        </p>

        <h3 className="text-base font-semibold mt-4">4. Gerir o seu consentimento</h3>
        <p>
          Pode retirar ou alterar o consentimento a qualquer momento no botão "Reset cookie
          preferences" acima, ou apagar cookies nas definições do navegador. Sem cookies
          essenciais o Serviço pode não funcionar corretamente.
        </p>

        <h3 className="text-base font-semibold mt-4">5. Alterações</h3>
        <p>Esta Política pode ser atualizada; a data acima reflete a versão em vigor.</p>
      </section>
    </main>
  );
}
